import React, {
  memo, useCallback, useEffect, useState,
} from 'react';

import PropTypes from 'prop-types';
import { TextField } from '@picsio/ui';

import localization from '../../shared/strings';
import Tooltip from '../Tooltip/Tooltip';
import ReadOnlyFieldTooltip from './helpers/ReadOnlyFieldTooltip';
import { useLocalFieldValue, useEnterToBlur } from './hooks';
import { productFieldInputProps } from './constants';
import styles from './styles.module.scss';

const validateTitle = (title) => {
  const trimmed = String(title).trim();

  return !trimmed
    ? { isValid: false, errorMessage: localization.PRODUCT_INFO.validation.productNameRequired }
    : { isValid: true };
};

const ProductName = ({
  value, readOnly, readOnlyTooltip, tooltip, onChange, isDiff, label,
}) => {
  const { localValue, setLocalValue } = useLocalFieldValue(value);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLocalValue(value);
    setError(null);
  }, [value, setLocalValue]);

  const handleChange = useCallback((e) => {
    if (readOnly) return;
    const nextValue = e.target.value;
    setLocalValue(nextValue);
    if (error && validateTitle(nextValue).isValid) setError(null);
  }, [error, readOnly, setLocalValue]);

  const handleBlur = useCallback(() => {
    if (readOnly) return;

    const trimmed = localValue.trim();
    const validation = validateTitle(trimmed);

    if (!validation.isValid) {
      setError(validation.errorMessage);
      setLocalValue(value);
      return;
    }

    setError(null);
    if (trimmed !== localValue) setLocalValue(trimmed);
    if (trimmed !== value) onChange?.(trimmed);
  }, [localValue, onChange, readOnly, setLocalValue, value]);

  const handleKeyPress = useEnterToBlur();

  return (
    <ReadOnlyFieldTooltip tooltip={tooltip || (readOnly ? readOnlyTooltip : null)}>
      <div className={styles.row}>
        <TextField
          value={localValue}
          disabled={readOnly}
          label={<Tooltip content={isDiff ? 'Unpublished changes' : null} placement="top"><span className={isDiff ? styles.diffDot : undefined}>{label}</span></Tooltip>}
          error={Boolean(error)}
          helperText={error}
          inputProps={productFieldInputProps}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyPress={handleKeyPress}
        />
      </div>
    </ReadOnlyFieldTooltip>
  );
};

ProductName.propTypes = {
  value: PropTypes.string,
  readOnly: PropTypes.bool,
  readOnlyTooltip: PropTypes.string,
  tooltip: PropTypes.string,
  isDiff: PropTypes.bool,
  label: PropTypes.node,
  onChange: PropTypes.func,
};

ProductName.defaultProps = {
  value: '',
  readOnly: false,
  readOnlyTooltip: null,
  tooltip: null,
  isDiff: false,
  label: 'Product name',
  onChange: null,
};

export default memo(ProductName);
